/*! Copyright (c) Safe As Milk. All rights reserved. */
import ModalDialog from "modal-dialog";

import { StateSwitch } from "utils";

class QuickShop extends ModalDialog {
    #boundFetchQuickShopOnProximity;
    #boundHandleDirectVariantControl;
    #boundHandleVariantInteraction;
    #boundMatchMediaChange;
    #boundOnCloseModal;
    #boundOnOpenModal;
    #boundOnVariantChange;
    #directVariantControls=[];
    #fetchingQuickShop=false;
    #loadObserver;
    #loadOn;
    #manualVariantSyncTimeout=null;
    #product;
    #stateSwitch=null;
    #variantId;
    constructor() {
        super();
        this.#variantId = null;
        this.#boundMatchMediaChange = this.#matchMediaChange.bind(this);
        this.#boundOnVariantChange = this.#onVariantChange.bind(this);
        this.#boundHandleVariantInteraction = this.#handleVariantInteraction.bind(this);
        this.#boundHandleDirectVariantControl = this.#handleDirectVariantControl.bind(this);
        this.#boundOnCloseModal = this.#onCloseModal.bind(this);
        this.#boundOnOpenModal = this.#onOpenModal.bind(this);
        this.#boundFetchQuickShopOnProximity = this.#fetchQuickShopOnProximity.bind(this);
    }
    connectedCallback() {
        super.connectedCallback();
        this.image = this.querySelector("img");
        this.imageContainerRatio = this.getAttribute("image-container-ratio").trim();
        this.imageFit = this.hasAttribute("image-fit");
        this.isPlaceholder = this.hasAttribute("placeholder");
        this.#loadOn = this.getAttribute("load-on") || "click";
        this.#loadObserver = null;
        if (!this.isPlaceholder) {
            this.#setProductData();
            this.variantSelector = this.querySelector("variant-selects, variant-radios, variant-mixed-inputs");
            if (this.variantSelector) {
                this.variantSelector.addEventListener("on:variant:change", this.#boundOnVariantChange);
            }
            this.#bindDirectVariantControls();
            this.#setupInitialVariantFilter();
        } else {
            this.#setUpFetchListeners();
        }
        this.addEventListener("change", this.#boundHandleVariantInteraction);
        this.addEventListener("click", this.#boundHandleVariantInteraction);
        this.on("on:modal:opening", this.#boundOnOpenModal).on("on:modal:closed", this.#boundOnCloseModal);
        const isInsideHoverModal = !!this.closest(".no-touchevents") && !!this.closest(".product-card--trigger-icon.product-card--hover");
        if (isInsideHoverModal) {
            this.#stateSwitch = new StateSwitch({
                min: "tab"
            });
            this.#stateSwitch.on(this.#boundMatchMediaChange);
        }
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        if (!this.isPlaceholder && this.variantSelector) {
            this.variantSelector.removeEventListener("on:variant:change", this.#boundOnVariantChange);
        } else {
            this.#removeFetchListeners();
        }
        this.#unbindDirectVariantControls();
        this.removeEventListener("change", this.#boundHandleVariantInteraction);
        this.removeEventListener("click", this.#boundHandleVariantInteraction);
        this.off("on:modal:opening", this.#boundOnOpenModal).off("on:modal:closed", this.#boundOnCloseModal);
        if (this.#stateSwitch) {
            this.#stateSwitch.off(this.#boundMatchMediaChange);
        }
    }
    set variantId(id) {
        this.#variantId = id;
        this.#updateVariant();
    }
    get variantId() {
        return this.#variantId;
    }
    async close() {
        return new Promise((async resolve => {
            if (this.closest(".is-inside-modal")) {
                await super.close();
                resolve("Closed");
            } else {
                resolve("Closed");
            }
        }));
    }
    #onOpenModal() {
        setTimeout((() => {
            this.classList.add("is-inside-modal");
            // Ensure the correct variant is selected when the modal opens so that
            // setOptionInputsAvailability() marks sold-out sizes with is-disabled.
            // Without this, getCurrentVariant() may fall back to the first variant
            // in the list (because no radio is pre-checked in the rendered HTML),
            // causing the wrong colour's availability to be shown.
            const pio = this.querySelector("product-interactive-options");
            if (pio) {
                let targetFound = false;
                const targetId = this.#variantId ? Number(this.#variantId) : Number(this.dataset.initialVariantId || "0") || null;
                if (targetId && pio.variants && typeof pio.getVariantById === "function") {
                    const v = pio.getVariantById(targetId);
                    if (v && typeof pio.selectVariant === "function") {
                        pio.selectVariant(v);
                        if (pio.variantsControlElement && typeof pio.variantsControlElement.onVariantChange === "function") {
                            pio.variantsControlElement.onVariantChange();
                        }
                        targetFound = true;
                    }
                }
                if (!targetFound && pio.variants && pio.variants.length > 0 && typeof pio.selectVariant === "function") {
                    const availableVariant = pio.variants.find(v => v.available) || pio.variants[0];
                    if (availableVariant) {
                        pio.selectVariant(availableVariant);
                        if (pio.variantsControlElement && typeof pio.variantsControlElement.onVariantChange === "function") {
                            pio.variantsControlElement.onVariantChange();
                        }
                        targetFound = true;
                    }
                }
                if (!targetFound && typeof pio.setOptionInputsAvailability === "function") {
                    pio.setOptionInputsAvailability();
                }
            }
            this.#syncOptionAvailabilityFromProduct();
        }));
    }
    #onCloseModal() {
        this.classList.remove("is-inside-modal");
        const productForm = this.querySelector("product-form");
        if (productForm) {
            productForm.removeError();
        }
    }
    async #onVariantChange(event) {
        const viewDetailsLink = this.querySelector(".js-view-details");
        const productLink = this.querySelector(".js-product-link");
        if (event.detail.variant) {
            if (this.dataset.productUrl) {
                this.#variantId = event.detail.variant.id;
                const newURL = this.dataset.productUrl.includes("?") ? this.dataset.productUrl.concat(`&variant=${this.#variantId}`) : this.dataset.productUrl.concat(`?variant=${this.#variantId}`);
                if (viewDetailsLink) viewDetailsLink.setAttribute("href", newURL);
                if (productLink) productLink.setAttribute("href", newURL);
            }
            let image = event.detail.variant.featured_image;
            if (!image) {
                image = this.#product.media && this.#product.media.length > 0 ? this.#product.media[0].preview_image : null;
            }
            const carousel = this.querySelector("media-carousel");
            if (image) {
                if (carousel && event.detail.variant.featured_media) {
                    if (!carousel.dataset.activeVariantOnly) {
                        const mediaId = event.detail.variant.featured_media.id;
                        const slide = carousel.querySelector(`[data-media-id="${mediaId}"]`);
                        if (slide && typeof carousel.slideTo === "function") {
                            carousel.slideTo(parseInt(slide.dataset.slideIndex, 10));
                        }
                    }
                } else if (this.image) {
                    this.#updateImage(image.src, image.width, image.height, this.image.getAttribute("alt"));
                }
            }
            if (carousel && carousel.dataset.activeVariantOnly) {
                await customElements.whenDefined("media-carousel");
                if (typeof carousel.filterByMediaIds === "function") {
                    const mediaId = event.detail.variant.featured_media.id;
                    const activeIds = this.#computeActiveMediaGroup(this.#product?.variant_media_ids ? this.#product.variant_media_ids[event.detail.variant?.id] : mediaId ?? null);
                    carousel.filterByMediaIds(activeIds);
                    const applySlideTo = () => {
                        const slide = carousel.querySelector(`[data-media-id="${mediaId}"]`);
                        if (slide && typeof carousel.slideTo === "function") {
                            carousel.slideTo(parseInt(slide.dataset.slideIndex || slide.dataset.slideId || "0", 10));
                        }
                    };
                    if (carousel.classList.contains("is-initialized")) applySlideTo();
                    else carousel.addEventListener("on:media-carousel:init", applySlideTo, { once: true });
                }
            }
        }
    }
    #handleVariantInteraction(event) {
        const target = event.target?.closest?.("input, select, label");
        if (!target) return;
        const variantSelector = this.querySelector("variant-selects, variant-radios, variant-mixed-inputs");
        if (!variantSelector || !variantSelector.contains(target)) return;
        if (event.type === "click" && target.tagName !== "LABEL") return;
        if (event.type === "change" && target.tagName === "INPUT" && target.type !== "radio") return;
        this.#scheduleManualVariantSync(`fallback:${event.type}`);
    }
    #bindDirectVariantControls() {
        this.#unbindDirectVariantControls();
        const controls = this.querySelectorAll("variant-radios label[for], variant-radios input[type=radio], variant-mixed-inputs input[type=radio], variant-mixed-inputs label[for], variant-selects select, variant-mixed-inputs select");
        controls.forEach((control => {
            const eventName = control.tagName === "SELECT" ? "change" : "click";
            control.addEventListener(eventName, this.#boundHandleDirectVariantControl, true);
            this.#directVariantControls.push({
                control,
                eventName
            });
        }));
    }
    #unbindDirectVariantControls() {
        this.#directVariantControls.forEach(({control, eventName}) => {
            control.removeEventListener(eventName, this.#boundHandleDirectVariantControl, true);
        });
        this.#directVariantControls = [];
    }
    #handleDirectVariantControl(event) {
        const variantSelector = this.querySelector("variant-selects, variant-radios, variant-mixed-inputs");
        if (!variantSelector) return;
        const target = event.currentTarget;
        let source = target;
        if (target.tagName === "LABEL") {
            const inputId = target.getAttribute("for");
            source = inputId ? this.querySelector(`#${CSS.escape(inputId)}`) : null;
            if (!source || source.disabled) return;
            source.checked = true;
        }
        if (!source || source.disabled) return;
        this.#scheduleManualVariantSync(`direct:${event.type}`);
    }
    #scheduleManualVariantSync(source) {
        if (this.#manualVariantSyncTimeout) {
            clearTimeout(this.#manualVariantSyncTimeout);
        }
        this.#manualVariantSyncTimeout = setTimeout((() => {
            this.#manualVariantSyncTimeout = null;
            this.#syncVariantFromControls(source);
        }));
    }
    #getSelectedOptions() {
        const variantSelector = this.querySelector("variant-selects, variant-radios, variant-mixed-inputs");
        if (!variantSelector) return [];
        if (variantSelector.tagName === "VARIANT-RADIOS") {
            return Array.from(variantSelector.querySelectorAll("fieldset")).map((fieldset => fieldset.querySelector("input:checked")?.value || null));
        }
        return Array.from(variantSelector.querySelectorAll("input:checked, select")).map((element => element.value || null));
    }
    #findVariantBySelectedOptions() {
        const options = this.#getSelectedOptions();
        if (!this.#product?.variants?.length || options.length === 0 || options.some((option => !option))) {
            return null;
        }
        return this.#product.variants.find((variant => variant.options.every(((option, index) => option === options[index])))) || null;
    }
    #syncVariantFromControls(source) {
        const variant = this.#findVariantBySelectedOptions();
        if (!variant) return;
        const productInteractiveOptions = this.querySelector("product-interactive-options");
        if (productInteractiveOptions && typeof productInteractiveOptions.selectVariant === "function") {
            productInteractiveOptions.selectVariant(variant);
        } else if (productInteractiveOptions && typeof productInteractiveOptions.setOptionInputsAvailability === "function") {
            productInteractiveOptions.currentVariant = variant;
            productInteractiveOptions.setOptionInputsAvailability();
        }
        const variantSelector = this.querySelector("variant-selects, variant-radios, variant-mixed-inputs");
        if (variantSelector) {
            variantSelector.currentVariant = variant;
            variantSelector.options = variant.options;
            if (typeof variantSelector.updateVariantInput === "function") {
                variantSelector.updateVariantInput();
            }
            if (typeof variantSelector.renderProductInfo === "function") {
                variantSelector.renderProductInfo();
            }
            if (typeof variantSelector.updateLabelsBlock === "function") {
                variantSelector.updateLabelsBlock();
            }
            if (typeof variantSelector.updatePickupAvailability === "function") {
                variantSelector.updatePickupAvailability();
            }
            if (typeof variantSelector.removeErrorMessage === "function") {
                variantSelector.removeErrorMessage();
            }
            variantSelector.dispatchEvent(new CustomEvent("on:variant:change", {
                detail: {
                    variant
                }
            }));
            this.#syncOptionAvailabilityFromProduct();
        } else {
            this.#onVariantChange({
                detail: {
                    variant
                }
            });
        }
    }
    #syncOptionAvailabilityFromProduct() {
        const productInteractiveOptions = this.querySelector("product-interactive-options");
        if (productInteractiveOptions && typeof productInteractiveOptions.setOptionInputsAvailability === "function") {
            productInteractiveOptions.setOptionInputsAvailability();
            return;
        }
        const variantSelector = this.querySelector("variant-radios, variant-mixed-inputs, variant-selects");
        if (!variantSelector) return;
        const selectedOptions = this.#getSelectedOptions();
        const fieldsets = Array.from(variantSelector.querySelectorAll("fieldset"));
        fieldsets.forEach(((fieldset, position) => {
            Array.from(fieldset.querySelectorAll('input[type="radio"]')).forEach((input => {
                const matchingVariants = (this.#product?.variants || []).filter((variant => {
                    if (variant.options[position] !== input.value) return false;
                    for (let index = 0; index < position; index += 1) {
                        if (selectedOptions[index] && variant.options[index] !== selectedOptions[index]) return false;
                    }
                    return true;
                }));
                const hasAvailableVariant = matchingVariants.some((variant => variant.available));
                const label = input.nextElementSibling;
                input.disabled = !hasAvailableVariant;
                if (label) {
                    label.classList.toggle("is-disabled", input.disabled);
                }
            }));
        }));
        Array.from(variantSelector.querySelectorAll("select")).forEach(((select, position) => {
            Array.from(select.options).forEach((option => {
                const matchingVariants = (this.#product?.variants || []).filter((variant => {
                    if (variant.options[position] !== option.value) return false;
                    for (let index = 0; index < position; index += 1) {
                        if (selectedOptions[index] && variant.options[index] !== selectedOptions[index]) return false;
                    }
                    return true;
                }));
                option.disabled = !matchingVariants.some((variant => variant.available));
            }));
        }));
    }
    #setProductData() {
        const productData = this.querySelector("[id^=ProductJson]");
        if (productData) this.#product = JSON.parse(productData.textContent);
        // Override media/variants with shsd-merged data so #computeActiveMediaGroup
        // uses the correct child-product media IDs (for shsd_parent products)
        const carouselData = this.querySelector("[id^=QuickShopMediaJson]");
        if (carouselData && this.#product) {
            const merged = JSON.parse(carouselData.textContent);
            this.#product = Object.assign({}, this.#product, { media: merged.media, variants: merged.variants, variant_media_ids: merged.variant_media_ids });
        }
    }
    #updateVariant() {
        if (!this.#variantId || this.isPlaceholder) return;
        const productInteractiveOptions = this.querySelector("product-interactive-options");
        if (productInteractiveOptions) {
            setTimeout((() => {
                const id = Number(this.#variantId);
                productInteractiveOptions.selectVariantById(id);
            }));
        }
    }
    #setUpFetchListeners() {
        if (!this.isPlaceholder) return;
        // Load content only when the modal is actually opened by the user.
        // Proximity pre-fetching (background preloading) has been intentionally
        // removed: sending multiple ?view=quick-shop requests in a short window
        // triggers Cloudflare bot-detection and blocks the entire storefront.
        this.on("on:modal:opening", (() => {
            if (this.isPlaceholder) this.#fetchQuickShop();
        }));
    }
    #removeFetchListeners() {
        if (this.#loadObserver) {
            this.#loadObserver.disconnect();
            this.#loadObserver = null;
        }
    }
    async #fetchQuickShopOnProximity(entries, observer) {}
    async #fetchQuickShop() {
        if (this.#fetchingQuickShop) return;
        const quickShopUrl = this.getAttribute("data-product-url");
        if (!quickShopUrl) return;
        try {
            this.dispatchEvent(new CustomEvent("on:quick-shop:loading"));
            this.#fetchingQuickShop = true;
            const htmlRaw = await fetch(`${quickShopUrl}${quickShopUrl.includes("?") ? "&" : "?"}view=quick-shop`);
            if (!htmlRaw.ok) throw new Error(`HTTP ${htmlRaw.status}`);
            let html = await htmlRaw.text();
            const idRegexp = /<quick-shop[\s]*id="([^"]*)"/m;
            const idMatch = html.match(idRegexp);
            if (!idMatch) throw new Error("Response did not contain quick-shop element (possible bot check page)");
            const [, originalQuickShopId] = idMatch;
            if (originalQuickShopId) {
                html = html.replaceAll(originalQuickShopId, this.id);
            }
            const parser = new DOMParser;
            const actualQuickShop = parser.parseFromString(html, "text/html").querySelector("quick-shop");
            this.#updateContent(actualQuickShop);
            this.dispatchEvent(new CustomEvent("on:quick-shop:loaded"));
        } catch (e) {
            this.dispatchEvent(new CustomEvent("on:quick-shop:failed"));
            throw new Error(`Could not fetch quick shop: "${e}"`);
        } finally {
            this.#fetchingQuickShop = false;
        }
    }
    #updateContent(newContent) {
        this.classList.add("is-loading-content");
        const productForm = newContent.querySelector("product-form");
        if (productForm && !this.hasAttribute("notify-on-add")) {
            productForm.removeAttribute("notify-on-add");
        }
        if (productForm && !this.hasAttribute("redirect-on-add")) {
            productForm.removeAttribute("redirect-on-add");
        }
        this.querySelector(".quick-shop__wrapper").innerHTML = newContent.querySelector(".quick-shop__wrapper").innerHTML;
        this.classList.remove("is-loading-content");
        const image = this.querySelector(".js-img-modal");
        if (image) image.setAttribute("sizes", this.getAttribute("image-sizes"));
        this.removeAttribute("placeholder");
        this.isPlaceholder = false;
        this.#setProductData();
        this.image = this.querySelector("img");
        this.variantSelector = this.querySelector("variant-selects, variant-radios, variant-mixed-inputs");
        if (this.variantSelector) {
            this.#updateVariant();
            this.variantSelector.addEventListener("on:variant:change", this.#boundOnVariantChange);
        }
        this.#bindDirectVariantControls();
        if (window.Shopify.PaymentButton) window.Shopify.PaymentButton.init();
        this.#removeFetchListeners();
        this.#setupInitialVariantFilter();
    }
    #computeActiveMediaGroup(featuredMediaId) {
        if (!this.#product || !featuredMediaId) return null;
        let variantMediaIdsArray = [];
        if (this.#product.variant_media_ids) {
            variantMediaIdsArray = Object.values(this.#product.variant_media_ids).filter(Boolean);
        } else {
            variantMediaIdsArray = (this.#product.variants || []).filter((v => v.featured_media)).map((v => v.featured_media.id));
        }
        const variantMediaIds = new Set(variantMediaIdsArray);
        let inGroup = false;
        const activeIds = new Set();
        for (const media of (this.#product.media || [])) {
            if (media.id === featuredMediaId) {
                inGroup = true;
            } else if (variantMediaIds.has(media.id)) {
                inGroup = false;
            }
            if (inGroup) {
                activeIds.add(media.id);
            }
        }
        return activeIds.size > 0 ? activeIds : null;
    }
    async #setupInitialVariantFilter() {
        const carousel = this.querySelector("media-carousel[data-active-variant-only]");
        if (!carousel) return;
        await customElements.whenDefined("media-carousel");
        const mediaId = parseInt(carousel.dataset.initialVariantMediaId || "0", 10) || null;
        if (!mediaId || !this.#product) return;
        const activeIds = this.#computeActiveMediaGroup(mediaId);
        if (typeof carousel.filterByMediaIds === "function") {
            carousel.filterByMediaIds(activeIds);
            const applySlideTo = () => {
                const slide = carousel.querySelector(`[data-media-id="${mediaId}"]`);
                if (slide && typeof carousel.slideTo === "function") {
                    carousel.slideTo(parseInt(slide.dataset.slideIndex || slide.dataset.slideId || "0", 10));
                }
            };
            if (carousel.classList.contains("is-initialized")) applySlideTo();
            else carousel.addEventListener("on:media-carousel:init", applySlideTo, { once: true });
        }
    }
    #updateImage(url, width, height, alt = "", srcsetWidths = [ 180, 360, 540, 720, 900, 1080, 1296, 1512 ]) {        if (!this.image || !url || !width || !height) return;
        const imageContainerRatio = this.imageContainerRatio !== "natural" ? this.imageContainerRatio.split(":").reduce(((ratio, value) => ratio !== 0 ? value / ratio : value), 0) : null;
        const aspectRatio = width / height;
        const uncroppedImage = this.imageContainerRatio === "natural" || imageContainerRatio && this.imageFit;
        let masterWidth = width;
        let masterHeight = height;
        if (!uncroppedImage) {
            masterHeight = Math.round(masterWidth * imageContainerRatio);
            if (masterHeight > height) {
                masterHeight = height;
                masterWidth = Math.round(masterHeight / imageContainerRatio);
            }
        }
        const srcset = srcsetWidths.reduce(((srcSet, srcWidth) => {
            const srcHeight = Math.round(uncroppedImage ? srcWidth / aspectRatio : srcWidth * imageContainerRatio);
            if (srcWidth > masterWidth || srcHeight > masterHeight) return srcSet;
            return `${srcSet}${url}${url.includes("?") ? "&" : "?"}width=${srcWidth}${!uncroppedImage ? `&height=${srcHeight}&crop=center` : ""} ${srcWidth}w ${srcHeight}h, `;
        }), "");
        const masterSrc = !uncroppedImage ? `${url}${url.includes("?") ? "&" : "?"}width=${masterWidth}&height=${masterHeight}&crop=center` : url;
        this.image.setAttribute("src", masterSrc);
        this.image.setAttribute("srcset", srcset);
        this.image.setAttribute("width", masterWidth);
        this.image.setAttribute("height", masterHeight);
        this.image.setAttribute("alt", alt);
    }
    #matchMediaChange({matches: matches}) {
        if (matches) this.close();
    }
}

customElements.define("quick-shop", QuickShop);
//# sourceMappingURL=quick-shop.js.map
